import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import analyticsService from '../services/analytics.service';

/**
 * Custom hook for fetching execution analytics data
 *
 * Provides:
 * - Execution trends
 * - Error analysis
 * - Performance metrics
 * - Cost analysis
 * - Template comparison
 * - Provider/model performance
 * - Loading and error states
 * - Refresh capabilities
 */
export const useExecutionAnalytics = (
  period = '7d',
  refreshInterval = null
) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [data, setData] = useState({
    trends: null,
    errors: null,
    performance: null,
    costs: null,
    templateComparison: null,
    providerModelPerformance: null,
  });
  const [loading, setLoading] = useState({
    trends: false,
    errors: false,
    performance: false,
    costs: false,
    templateComparison: false,
    providerModelPerformance: false,
  });
  const [error, setError] = useState({
    trends: null,
    errors: null,
    performance: null,
    costs: null,
    templateComparison: null,
    providerModelPerformance: null,
  });

  const fetchTrends = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated || authLoading) {
      return null;
    }
    try {
      setLoading(prev => ({ ...prev, trends: true }));
      setError(prev => ({ ...prev, trends: null }));
      const response = await analyticsService.getExecutionTrends({ period });
      setData(prev => ({ ...prev, trends: response }));
      return response;
    } catch (err) {
      setError(prev => ({ ...prev, trends: err.message }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, trends: false }));
    }
  }, [period, isAuthenticated, authLoading]);

  const fetchErrors = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated || authLoading) {
      return null;
    }
    try {
      setLoading(prev => ({ ...prev, errors: true }));
      setError(prev => ({ ...prev, errors: null }));
      const response = await analyticsService.getErrorAnalysis({ period });
      setData(prev => ({ ...prev, errors: response }));
      return response;
    } catch (err) {
      setError(prev => ({ ...prev, errors: err.message }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, errors: false }));
    }
  }, [period, isAuthenticated, authLoading]);

  const fetchPerformance = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated || authLoading) {
      return null;
    }
    try {
      setLoading(prev => ({ ...prev, performance: true }));
      setError(prev => ({ ...prev, performance: null }));
      const response = await analyticsService.getPerformanceMetrics({ period });
      setData(prev => ({ ...prev, performance: response }));
      return response;
    } catch (err) {
      setError(prev => ({ ...prev, performance: err.message }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, performance: false }));
    }
  }, [period, isAuthenticated, authLoading]);

  const fetchCosts = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated || authLoading) {
      return null;
    }
    try {
      setLoading(prev => ({ ...prev, costs: true }));
      setError(prev => ({ ...prev, costs: null }));
      const response = await analyticsService.getCostAnalysis({ period });
      setData(prev => ({ ...prev, costs: response }));
      return response;
    } catch (err) {
      setError(prev => ({ ...prev, costs: err.message }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, costs: false }));
    }
  }, [period, isAuthenticated, authLoading]);

  const fetchTemplateComparison = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated || authLoading) {
      return null;
    }
    try {
      setLoading(prev => ({ ...prev, templateComparison: true }));
      setError(prev => ({ ...prev, templateComparison: null }));
      const response = await analyticsService.getTemplateComparison({ period });
      setData(prev => ({ ...prev, templateComparison: response }));
      return response;
    } catch (err) {
      setError(prev => ({ ...prev, templateComparison: err.message }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, templateComparison: false }));
    }
  }, [period, isAuthenticated, authLoading]);

  const fetchProviderModelPerformance = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated || authLoading) {
      return null;
    }
    try {
      setLoading(prev => ({ ...prev, providerModelPerformance: true }));
      setError(prev => ({ ...prev, providerModelPerformance: null }));
      const response = await analyticsService.getProviderModelPerformance({
        period,
      });
      setData(prev => ({ ...prev, providerModelPerformance: response }));
      return response;
    } catch (err) {
      setError(prev => ({ ...prev, providerModelPerformance: err.message }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, providerModelPerformance: false }));
    }
  }, [period, isAuthenticated, authLoading]);

  const fetchAll = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated || authLoading) {
      return;
    }
    await Promise.all([
      fetchTrends(),
      fetchErrors(),
      fetchPerformance(),
      fetchCosts(),
      fetchTemplateComparison(),
      fetchProviderModelPerformance(),
    ]);
  }, [
    fetchTrends,
    fetchErrors,
    fetchPerformance,
    fetchCosts,
    fetchTemplateComparison,
    fetchProviderModelPerformance,
    isAuthenticated,
    authLoading,
  ]);

  // Initial load - only fetch when authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchAll();
    }
  }, [fetchAll, isAuthenticated, authLoading]);

  // Auto-refresh if refreshInterval is set
  useEffect(() => {
    if (!refreshInterval || !isAuthenticated || authLoading) return;

    const interval = setInterval(() => {
      fetchAll();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [fetchAll, refreshInterval, isAuthenticated, authLoading]);

  return {
    // Data
    data,
    // Loading states
    loading,
    // Error states
    error,
    // Individual fetch functions
    fetchTrends,
    fetchErrors,
    fetchPerformance,
    fetchCosts,
    fetchTemplateComparison,
    fetchProviderModelPerformance,
    // Fetch all
    fetchAll,
    // Refresh
    refresh: fetchAll,
  };
};

export default useExecutionAnalytics;
